import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Shown while the carrier's label PDF is still downloading. Deliberately blue, not red:
// the shipment was accepted and the label exists — the operator is waiting, not failing.
// This is the CONCURRENCY palette the rest of the error surfaces already use for waits.
//
// It carries no retry button on purpose. The PDF arrives on its own (measured p50 17s,
// 94.9% inside a minute) and the screen finishes the job unattended; offering a button
// invites exactly the re-click loop this replaced.
// A Blue Express shipment of more than 100 boxes is not a PDF download: the label is emitted
// and its pages fetched 100 at a time on the server, which can take a couple of minutes. It
// gets one instruction of its own instead of a "few seconds" promise it would break.
export const WAIT_REASON_BLUE_EXPRESS_LARGE_SHIPMENT = 'blue_express_large_shipment'
// A manual order packed in several boxes on a method that cannot mint one guide per box: the
// server is still choosing the method it moves to, so no carrier has accepted anything yet.
export const WAIT_REASON_MULTIPACKAGE_SWITCH = 'multipackage_switch'

const COPY_KEY_BY_WAIT_REASON = {
  [WAIT_REASON_BLUE_EXPRESS_LARGE_SHIPMENT]: 'generate_shipment.label_pending.blue_express_large_shipment',
  [WAIT_REASON_MULTIPACKAGE_SWITCH]: 'generate_shipment.label_pending.multipackage_switch',
}

export const LabelPendingBanner = ({ estimatedSeconds, startedAt, waitReason }) => {
  const { t } = useTranslation()
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!startedAt) return undefined
    const tick = () => setElapsed(Math.floor((Date.now() - startedAt) / 1000))
    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [startedAt])

  // Past the estimate the countdown would read as a broken promise, so switch to a
  // plain "still working" line rather than showing a negative or frozen number.
  const overdue = estimatedSeconds != null && elapsed >= estimatedSeconds
  const copyKey = COPY_KEY_BY_WAIT_REASON[waitReason] || 'generate_shipment.label_pending'

  return (
    <div role="status" aria-live="polite" className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 flex items-start">
      {/* motion-safe: whoever asked for reduced motion sees the ring still. The state
          is already conveyed by the colour, the text and the aria-live; the spin only accompanies it. */}
      <svg className="motion-safe:animate-spin h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
      <div className="flex-1">
        <p className="text-sm font-medium text-blue-800">{t(`${copyKey}.title`)}</p>
        <p className="text-sm text-blue-700 mt-1">{overdue ? t(`${copyKey}.still_working`) : t(`${copyKey}.subtitle`)}</p>
      </div>
    </div>
  )
}

export default LabelPendingBanner
